// Confirmation gate for a custom action button. When an action is configured
// with a confirmation (see ActionDef.confirmation), the engine answers the
// first run_action call with handler='confirm' INSTEAD of running the effect:
// it describes the prompt (and, for the one-time-code mode, has already emailed
// a code). This modal collects the user's answer and re-calls run_action with
// it; the engine verifies server-side and only then runs the effect. A wrong
// answer comes back as a 422 which we show inline so the user can retry — the
// modal owns the whole verify loop so every action runner (the open-record
// bar, the list toolbar, row menus, embeds) wires it the same way.
//
// Portal + z-index mirror WarningsConfirmModal: this opens from inside panels
// that form their own stacking contexts, so an inline overlay would be trapped.

import ActionErrorModal from './ActionErrorModal'
import { useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ActionConfirmInfo, RunActionResponse } from './contracts'
import { tw } from '../testHooks'

export interface ConfirmPayload {
  acknowledged?: boolean
  answer?: string
  resend?: boolean
}

export interface ConfirmHelpers {
  // Phase B — re-run the action with the user's answer; resolves with the
  // final (non-confirm) response or throws a 422 on a wrong answer.
  submit: (payload: ConfirmPayload) => Promise<RunActionResponse>
  // OTP only — email a fresh code; resolves with a new handler='confirm' response.
  resend: () => Promise<RunActionResponse>
}

const CANCELLED = '__actionConfirmCancelled'

/** True when an error is the "user cancelled the confirmation" sentinel — the
 *  caller should quietly stop, not surface an "Action failed" toast. */
export function isActionConfirmCancelled(err: unknown): boolean {
  return !!(err && typeof err === 'object' && (err as Record<string, unknown>)[CANCELLED])
}

/**
 * Drive an action's confirmation gate. Calls `attempt()` (phase A); if the
 * engine asks for confirmation, opens the modal via `openConfirm` and lets it
 * run the verify loop, returning the final effect response. Compose INSIDE the
 * warning-confirm wrapper so a post-confirm warning still prompts:
 *
 *   const res = await attemptActionWithConfirm(
 *     (confirm) => attemptWithWarningConfirm(
 *       (ignore) => runtime.runAction(p, o, { ...req,
 *         ...(ignore.length ? { ignoreWarnings: ignore } : {}),
 *         ...(confirm ? { confirm } : {}) }),
 *       confirmWarnings),
 *     confirmAction)
 */
export async function attemptActionWithConfirm(
  attempt: (confirm?: ConfirmPayload) => Promise<RunActionResponse>,
  openConfirm: (info: ActionConfirmInfo, helpers: ConfirmHelpers) => Promise<RunActionResponse>,
): Promise<RunActionResponse> {
  const res = await attempt()
  if (res?.handler !== 'confirm' || !res.confirm) return res
  return await openConfirm(res.confirm, {
    submit: (payload) => attempt(payload),
    resend: () => attempt({ resend: true }),
  })
}

// Pull a human message out of either error envelope (designer/site axios or
// standalone-runtime fetch) so a wrong code / password reads clearly.
function confirmErrorMessage(err: unknown): string {
  const e = err as {
    response?: { data?: { error?: { message?: string; details?: { errors?: Array<{ message?: string }> } } } }
    detail?: { summary?: string; errors?: Array<{ message?: string }> }
  }
  const env = e?.response?.data?.error
  if (env) {
    const first = env.details?.errors?.[0]?.message
    if (first) return first
    if (env.message) return env.message
  }
  const d = e?.detail
  if (d) {
    const first = d.errors?.[0]?.message
    if (first) return first
    if (d.summary) return d.summary
  }
  return 'Could not confirm. Please try again.'
}

interface Props {
  info: ActionConfirmInfo
  helpers: ConfirmHelpers
  onResolve: (res: RunActionResponse) => void
  onCancel: () => void
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 12000,
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: '0.9225rem', borderRadius: 6,
  border: '1px solid var(--color-border, #ccc)', background: 'var(--color-bg, #fff)',
  color: 'var(--color-text, #111)', boxSizing: 'border-box',
}

// True when a failed submit is the ACTION's own error (its business logic or a
// Vision bean refused it) rather than the confirmation check (wrong code /
// password / phrase, rule "confirmation"). Retrying cannot change the former,
// so the gate ends there with just OK.
function isActionFailure(err: unknown): boolean {
  const e = err as {
    response?: { data?: { error?: { details?: { errors?: Array<{ rule?: string }> } }
      detail?: { errors?: Array<{ rule?: string }> } } }
    detail?: { errors?: Array<{ rule?: string }> }
  }
  const errors = e?.response?.data?.error?.details?.errors
    ?? e?.response?.data?.detail?.errors
    ?? e?.detail?.errors
  return Array.isArray(errors) && errors.length > 0
    && !errors.some(x => x?.rule === 'confirmation')
}

export default function ActionConfirmModal({ info: initialInfo, helpers, onResolve, onCancel }: Props) {
  if (typeof document === 'undefined') return null // SSR safety
  const [info, setInfo] = useState<ActionConfirmInfo>(initialInfo)
  const [answer, setAnswer] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // The action ran and failed: show its error with only OK — the process stops.
  const [fatal, setFatal] = useState<string | null>(null)

  const needsAnswer = info.mode !== 'reconfirm'
  const canSubmit = !busy && (!needsAnswer || answer.trim() !== '')

  const doSubmit = async () => {
    if (!canSubmit) return
    const payload: ConfirmPayload = info.mode === 'reconfirm'
      ? { acknowledged: true }
      : { answer: answer.trim() }
    setBusy(true); setError(null); setNotice(null)
    try {
      const res = await helpers.submit(payload)
      if (res?.handler === 'confirm' && res.confirm) {
        // Unusual, but stay on the gate if the engine re-prompts.
        setInfo(res.confirm); setAnswer(''); setBusy(false)
        return
      }
      onResolve(res)
    } catch (err) {
      if (isActionFailure(err)) setFatal(confirmErrorMessage(err))
      else setError(confirmErrorMessage(err))
      setBusy(false)
    }
  }

  const doResend = async () => {
    setBusy(true); setError(null); setNotice(null)
    try {
      const res = await helpers.resend()
      if (res?.handler === 'confirm' && res.confirm) setInfo(res.confirm)
      setNotice('A new code has been sent.')
    } catch {
      setError('Could not resend the code. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canSubmit) { e.preventDefault(); void doSubmit() }
  }

  const confirmLabel = info.mode === 'reconfirm' ? 'Yes, continue' : 'Confirm'

  if (fatal) return <ActionErrorModal messages={[fatal]} onClose={onCancel} />

  return createPortal(
    <div style={overlayStyle} onClick={busy ? undefined : onCancel}>
      <div
        onClick={e => e.stopPropagation()}
        data-tw-dialog="action_confirm"
        style={{
          background: 'var(--color-surface, #fff)', color: 'var(--color-text, #111)',
          borderRadius: 8, width: 'min(460px, 94vw)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 16px', borderBottom: '1px solid var(--color-border, #e5e5e5)',
        }}>
          <span style={{ fontWeight: 600, fontSize: '0.9625rem' }}>Please confirm</span>
          <button
            onClick={onCancel} disabled={busy}
            {...tw('action', 'close')}
            style={{ background: 'none', border: 'none', cursor: busy ? 'default' : 'pointer', fontSize: '1.1625rem', color: 'var(--color-text-secondary, #666)' }}
            title="Cancel">×</button>
        </div>

        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: '0.9225rem', lineHeight: 1.45 }}>{info.message}</div>

          {info.mode === 'phrase' && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary, #666)' }}>
                Type {info.phrase_label || 'the required value'} to confirm
              </span>
              <input
                autoFocus style={inputStyle} value={answer}
                onChange={e => setAnswer(e.target.value)} onKeyDown={onKeyDown}
                placeholder={info.phrase_label || ''} />
            </label>
          )}

          {info.mode === 'otp' && (
            <>
              <div style={{ fontSize: '0.8425rem', color: 'var(--color-text-secondary, #666)' }}>
                {info.otp_error
                  ? <span style={{ color: 'var(--color-danger, #dc2626)' }}>{info.otp_error}</span>
                  : (info.otp_sent
                    ? <>We emailed a one-time code{info.otp_to ? <> to <strong>{info.otp_to}</strong></> : ''}.</>
                    : 'Enter the one-time code.')}
              </div>
              <input
                autoFocus style={inputStyle} value={answer} inputMode="numeric"
                onChange={e => setAnswer(e.target.value)} onKeyDown={onKeyDown}
                placeholder="6-digit code" />
              <button
                onClick={doResend} disabled={busy}
                {...tw('action', 'resend')}
                style={{ alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, fontSize: '0.8225rem', color: 'var(--color-accent, #4c8dff)', cursor: busy ? 'default' : 'pointer' }}>
                Resend code
              </button>
            </>
          )}

          {info.mode === 'password' && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary, #666)' }}>
                Your account password
              </span>
              <input
                autoFocus type="password" style={inputStyle} value={answer}
                onChange={e => setAnswer(e.target.value)} onKeyDown={onKeyDown}
                autoComplete="current-password" />
            </label>
          )}

          {notice && (
            <div style={{ fontSize: '0.8225rem', color: 'var(--color-accent, #0a7d6b)' }}>{notice}</div>
          )}
          {error && (
            <div style={{ fontSize: '0.8425rem', color: 'var(--color-danger, #dc2626)' }}>{error}</div>
          )}
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '10px 16px', borderTop: '1px solid var(--color-border, #e5e5e5)',
        }}>
          <button
            onClick={onCancel} disabled={busy}
            {...tw('action', 'cancel')}
            style={{ padding: '6px 14px', fontSize: '0.8625rem', borderRadius: 6, border: '1px solid var(--color-border, #ccc)', background: 'transparent', cursor: busy ? 'default' : 'pointer', color: 'var(--color-text, #111)' }}>
            Cancel
          </button>
          <button
            onClick={doSubmit} disabled={!canSubmit}
            {...tw('action', 'confirm')}
            style={{ padding: '6px 14px', fontSize: '0.8625rem', borderRadius: 6, border: 'none', background: canSubmit ? 'var(--color-accent, #4c8dff)' : 'var(--color-border, #ccc)', color: '#fff', cursor: canSubmit ? 'pointer' : 'default' }}>
            {busy ? 'Checking…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

interface Pending {
  info: ActionConfirmInfo
  helpers: ConfirmHelpers
  resolve: (res: RunActionResponse) => void
  reject: (err: unknown) => void
}

/**
 * Stateful wrapper for the action-confirmation flow. `confirmAction` opens the
 * modal and resolves with the final effect response once the user confirms (or
 * rejects with the cancel sentinel — test it with `isActionConfirmCancelled`);
 * render `actionConfirmModal` somewhere in the host tree. Pass `confirmAction`
 * as the second argument to `attemptActionWithConfirm`.
 */
export function useActionConfirm(): {
  confirmAction: (info: ActionConfirmInfo, helpers: ConfirmHelpers) => Promise<RunActionResponse>
  actionConfirmModal: React.ReactNode
} {
  const [pending, setPending] = useState<Pending | null>(null)

  const confirmAction = useCallback((info: ActionConfirmInfo, helpers: ConfirmHelpers) => {
    return new Promise<RunActionResponse>((resolve, reject) => {
      setPending({ info, helpers, resolve, reject })
    })
  }, [])

  const resolvePending = (res: RunActionResponse) => {
    setPending((p) => { p?.resolve(res); return null })
  }
  const cancelPending = () => {
    setPending((p) => {
      p?.reject(Object.assign(new Error('confirmation cancelled'), { [CANCELLED]: true }))
      return null
    })
  }

  const actionConfirmModal = pending ? (
    <ActionConfirmModal
      info={pending.info}
      helpers={pending.helpers}
      onResolve={resolvePending}
      onCancel={cancelPending}
    />
  ) : null

  return { confirmAction, actionConfirmModal }
}
